import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Calculator,
  Download,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Landmark,
  ArrowUpRight,
  ArrowDownRight,
  ShieldAlert,
  Tags,
  FileSpreadsheet,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const zar = (n) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const csvEscape = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function VatReport() {
  const now = new Date();
  const currentYear = now.getFullYear();

  const [year, setYear] = useState(currentYear);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("custom");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [report, setReport] = useState(null);
  const [extras, setExtras] = useState(null); // Category × VAT breakdown + non-compliant zero-rated audit
  const [loading, setLoading] = useState(false);

  // Fetch category-A periods for the chosen year
  useEffect(() => {
    axios
      .get(`${API}/api/vat-report/periods?year=${year}`)
      .then((res) => {
        const p = res.data?.periods || [];
        setPeriods(p);
        // If we have a "current period" and no custom selection yet, default to it.
        const current = res.data?.current_period;
        if (current && selectedPeriodKey === "custom" && !start && !end) {
          setSelectedPeriodKey(current.label);
          setStart(current.start);
          setEnd(current.end);
        }
      })
      .catch(() => toast.error("Failed to load VAT periods"));
  }, [year]);

  const handlePeriodChange = (val) => {
    setSelectedPeriodKey(val);
    if (val === "custom") return;
    const p = periods.find((x) => x.label === val);
    if (p) {
      setStart(p.start);
      setEnd(p.end);
    }
  };

  const runReport = async () => {
    if (!start || !end) {
      toast.error("Pick a period or set a custom range first.");
      return;
    }
    setLoading(true);
    setReport(null);
    setExtras(null);
    try {
      const [main, extra] = await Promise.all([
        axios.get(`${API}/api/vat-report?start_date=${start}&end_date=${end}`),
        axios
          .get(`${API}/api/vat-report/category-breakdown?start_date=${start}&end_date=${end}`)
          .catch(() => null),
      ]);
      setReport(main.data);
      if (extra?.data) setExtras(extra.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load VAT report");
    } finally {
      setLoading(false);
    }
  };

  const downloadCategoryXlsx = () => {
    if (!start || !end) return;
    window.open(
      `${API}/api/vat-report/category-breakdown/xlsx?start_date=${start}&end_date=${end}`,
      "_blank",
    );
  };

  // ------------- Exports -------------

  const downloadCsv = () => {
    if (!report) return;
    const rows = [
      ["Bosveld Bou & Hardware — SARS VAT201 Report"],
      ["Period", report.period.label],
      ["Days", report.period.days],
      [],
      ["SARS VAT201 Blocks", "Amount (R)"],
      ["Block 1 — Standard-rated supplies (INCL VAT)", report.vat201.block_1_standard_supplies_incl_vat.toFixed(2)],
      ["Block 2 — Zero-rated supplies", report.vat201.block_2_zero_rated_supplies.toFixed(2)],
      ["Block 3 — Exempt supplies", (report.vat201.block_3_exempt_supplies || 0).toFixed(2)],
      ["Block 4 — Total supplies (INCL VAT)", report.vat201.block_4_total_supplies_incl_vat.toFixed(2)],
      ["Block 4A — Output VAT", report.vat201.block_4a_output_vat.toFixed(2)],
      ["Block 14A — Standard-rated purchases (INCL VAT)", report.vat201.block_14a_standard_purchases_incl_vat.toFixed(2)],
      ["Block 15 — Input VAT", report.vat201.block_15_input_vat.toFixed(2)],
      ["Block 20 — Net VAT payable / (refund)", report.vat201.block_20_net_vat_payable.toFixed(2)],
      [],
      ["Sales breakdown"],
      ["Gross sales (INCL VAT, net of refunds)", report.sales.gross_incl_vat.toFixed(2)],
      ["Standard-rated (INCL VAT)", report.sales.standard_rated_incl_vat.toFixed(2)],
      ["Standard-rated (EXCL VAT)", report.sales.standard_rated_excl_vat.toFixed(2)],
      ["Zero-rated", report.sales.zero_rated.toFixed(2)],
      ["Exempt supplies", (report.sales.exempt || 0).toFixed(2)],
      ["Refunds (INCL VAT)", report.sales.refunds_incl_vat.toFixed(2)],
      ["Refund VAT credit", report.sales.refund_vat_credit.toFixed(2)],
      ["Output VAT", report.sales.output_vat.toFixed(2)],
      ["Sale count", report.sales.sale_count],
      ["Refund count", report.sales.refund_count],
      [],
      ["Purchases breakdown"],
      ["Total (INCL VAT)", report.purchases.total_incl_vat.toFixed(2)],
      ["Standard-rated (INCL VAT)", report.purchases.standard_rated_incl_vat.toFixed(2)],
      ["Zero-rated (INCL VAT)", report.purchases.zero_rated_incl_vat.toFixed(2)],
      ["Input VAT", report.purchases.input_vat.toFixed(2)],
      ["GRV count", report.purchases.grv_count],
      [],
      ["Audit Pack reconciliation"],
    ];
    if (report.reconciliation?.attempted && report.reconciliation.output_vat) {
      rows.push(
        ["", "VAT Report", "Audit Pack", "Diff", "Match?"],
        [
          "Output VAT",
          report.reconciliation.output_vat.vat_report.toFixed(2),
          report.reconciliation.output_vat.audit_pack.toFixed(2),
          report.reconciliation.output_vat.diff.toFixed(2),
          report.reconciliation.output_vat.matches ? "YES" : "NO",
        ],
        [
          "Input VAT",
          report.reconciliation.input_vat.vat_report.toFixed(2),
          report.reconciliation.input_vat.audit_pack.toFixed(2),
          report.reconciliation.input_vat.diff.toFixed(2),
          report.reconciliation.input_vat.matches ? "YES" : "NO",
        ],
      );
    } else {
      rows.push(["Reconciliation not attempted", report.reconciliation?.reason || ""]);
    }
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vat-report-${report.period.start}-to-${report.period.end}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  };

  const downloadPdf = () => {
    if (!report) return;
    // Render an inline print-friendly document and open the browser print
    // dialog — the user can then "Save as PDF" or send straight to a printer.
    // window.open() is synchronous so we don't need a loading state.
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) {
      toast.error("Pop-up blocked — allow pop-ups to print the VAT report.");
      return;
    }
    const rec = report.reconciliation;
      const recRow = rec?.attempted && rec.output_vat
        ? `
          <tr>
            <td>Output VAT</td>
            <td class="num">${zar(rec.output_vat.vat_report)}</td>
            <td class="num">${zar(rec.output_vat.audit_pack)}</td>
            <td class="num">${zar(rec.output_vat.diff)}</td>
            <td>${rec.output_vat.matches ? "✅ Match" : "❌ Diff"}</td>
          </tr>
          <tr>
            <td>Input VAT</td>
            <td class="num">${zar(rec.input_vat.vat_report)}</td>
            <td class="num">${zar(rec.input_vat.audit_pack)}</td>
            <td class="num">${zar(rec.input_vat.diff)}</td>
            <td>${rec.input_vat.matches ? "✅ Match" : "❌ Diff"}</td>
          </tr>`
        : `<tr><td colspan="5" class="muted">Reconciliation not attempted for multi-month ranges. Pick a single calendar month to reconcile against the Audit Pack.</td></tr>`;

      w.document.write(`
<!doctype html><html><head><title>VAT201 — ${report.period.label}</title>
<style>
  body { font-family: -apple-system, Arial, sans-serif; padding: 32px; color: #111; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 14px; margin: 28px 0 8px; text-transform: uppercase; color: #444; letter-spacing: 0.05em; border-bottom: 2px solid #333; padding-bottom: 4px; }
  .meta { color: #555; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 12px; }
  th, td { padding: 7px 10px; border-bottom: 1px solid #ddd; text-align: left; }
  th { background: #f4f4f4; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .total { background: #fef7e0; font-weight: 700; }
  .muted { color: #777; font-style: italic; }
  .badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
  .payable { background: #fee2e2; color: #991b1b; }
  .refund { background: #d1fae5; color: #065f46; }
  .disclaimer { margin-top: 30px; font-size: 10px; color: #777; }
</style></head><body>
<h1>Bosveld Bou &amp; Hardware — SARS VAT201 Report</h1>
<div class="meta">
  Period: <strong>${report.period.label}</strong> (${report.period.days} days)<br/>
  Generated: ${new Date().toLocaleString("en-ZA")}
</div>

<h2>SARS VAT201 Blocks</h2>
<table>
  <tr><th>Line</th><th>Description</th><th class="num">Amount (R)</th></tr>
  <tr><td>1</td><td>Standard-rated supplies (INCL VAT)</td><td class="num">${zar(report.vat201.block_1_standard_supplies_incl_vat)}</td></tr>
  <tr><td>2</td><td>Zero-rated supplies</td><td class="num">${zar(report.vat201.block_2_zero_rated_supplies)}</td></tr>
  <tr><td>3</td><td>Exempt supplies</td><td class="num">${zar(report.vat201.block_3_exempt_supplies || 0)}</td></tr>
  <tr><td>4</td><td>Total supplies (INCL VAT)</td><td class="num">${zar(report.vat201.block_4_total_supplies_incl_vat)}</td></tr>
  <tr><td>4A</td><td><strong>Output VAT</strong></td><td class="num"><strong>${zar(report.vat201.block_4a_output_vat)}</strong></td></tr>
  <tr><td>14A</td><td>Standard-rated purchases (INCL VAT)</td><td class="num">${zar(report.vat201.block_14a_standard_purchases_incl_vat)}</td></tr>
  <tr><td>15</td><td><strong>Input VAT</strong></td><td class="num"><strong>${zar(report.vat201.block_15_input_vat)}</strong></td></tr>
  <tr class="total"><td>20</td><td>Net VAT ${report.vat201.block_20_net_vat_payable >= 0 ? "payable to SARS" : "refund from SARS"}</td><td class="num">${zar(Math.abs(report.vat201.block_20_net_vat_payable))} <span class="badge ${report.vat201.block_20_net_vat_payable >= 0 ? "payable" : "refund"}">${report.vat201.block_20_net_vat_payable >= 0 ? "PAYABLE" : "REFUND"}</span></td></tr>
</table>

<h2>Sales Breakdown</h2>
<table>
  <tr><th>Description</th><th class="num">Amount (R)</th></tr>
  <tr><td>Gross sales (INCL VAT, net of refunds)</td><td class="num">${zar(report.sales.gross_incl_vat)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which standard-rated (INCL VAT)</td><td class="num">${zar(report.sales.standard_rated_incl_vat)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which standard-rated (EXCL VAT)</td><td class="num">${zar(report.sales.standard_rated_excl_vat)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which zero-rated</td><td class="num">${zar(report.sales.zero_rated)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which exempt</td><td class="num">${zar(report.sales.exempt || 0)}</td></tr>
  <tr><td>Refunds (INCL VAT)</td><td class="num">${zar(report.sales.refunds_incl_vat)}</td></tr>
  <tr><td>Refund VAT credit</td><td class="num">${zar(report.sales.refund_vat_credit)}</td></tr>
  <tr class="total"><td>Output VAT (Line 4A)</td><td class="num">${zar(report.sales.output_vat)}</td></tr>
  <tr><td class="muted">Sale documents / refund documents</td><td class="num muted">${report.sales.sale_count} / ${report.sales.refund_count}</td></tr>
</table>

<h2>Purchases (GRVs) Breakdown</h2>
<table>
  <tr><th>Description</th><th class="num">Amount (R)</th></tr>
  <tr><td>Total (INCL VAT)</td><td class="num">${zar(report.purchases.total_incl_vat)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which standard-rated (INCL VAT)</td><td class="num">${zar(report.purchases.standard_rated_incl_vat)}</td></tr>
  <tr><td>&nbsp;&nbsp;of which zero-rated</td><td class="num">${zar(report.purchases.zero_rated_incl_vat)}</td></tr>
  <tr class="total"><td>Input VAT (Line 15)</td><td class="num">${zar(report.purchases.input_vat)}</td></tr>
  <tr><td class="muted">GRV documents included</td><td class="num muted">${report.purchases.grv_count}</td></tr>
</table>

<h2>Audit Pack Reconciliation</h2>
<table>
  <tr><th>Metric</th><th class="num">VAT Report</th><th class="num">Audit Pack</th><th class="num">Diff</th><th>Status</th></tr>
  ${recRow}
</table>

<div class="disclaimer">
  Numbers on this report are generated from Bosveld's live POS &amp; GRV data. This report is intended as a submission-preparation aid — verify against your own accounting records before filing VAT201 with SARS.
</div>

<script>window.onload = () => { window.print(); };</script>
</body></html>`);
    w.document.close();
  };

  // ------------- Derived helpers -------------
  const netPayable = report?.vat201?.block_20_net_vat_payable ?? 0;
  const isPayable = netPayable >= 0;
  const rec = report?.reconciliation;
  const recPassed = rec?.attempted && rec.matches === true;

  const yearOptions = useMemo(() => {
    const out = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) out.push(y);
    return out;
  }, [currentYear]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="vat-report-page">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="h-8 w-8 text-blue-600" />
            SARS VAT201 Report
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            One-screen VAT201 preparation — pick a period, print for the accountant, and cross-check against the Audit Pack.
          </p>
        </div>
      </div>

      {/* ── Period picker ──────────────────────────────────────────── */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
            <div>
              <Label className="text-xs">Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="vat-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">SARS Category-A period</Label>
              <Select value={selectedPeriodKey} onValueChange={handlePeriodChange}>
                <SelectTrigger data-testid="vat-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom range…</SelectItem>
                  {periods.map((p) => (
                    <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Start date</Label>
              <Input
                type="date"
                value={start}
                onChange={(e) => { setStart(e.target.value); setSelectedPeriodKey("custom"); }}
                data-testid="vat-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">End date</Label>
              <Input
                type="date"
                value={end}
                onChange={(e) => { setEnd(e.target.value); setSelectedPeriodKey("custom"); }}
                data-testid="vat-end-date"
              />
            </div>
            <Button onClick={runReport} disabled={loading} data-testid="vat-run-btn" className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Results ────────────────────────────────────────────────── */}
      {report && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-blue-200">
              <CardContent className="pt-6">
                <div className="text-xs uppercase tracking-wide text-blue-800 flex items-center gap-1">
                  <ArrowUpRight className="h-4 w-4" /> Output VAT (Line 4A)
                </div>
                <div className="text-3xl font-bold text-blue-700 mt-1" data-testid="output-vat-total">
                  {zar(report.summary.output_vat)}
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  VAT collected on standard-rated sales (net of refunds).
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200">
              <CardContent className="pt-6">
                <div className="text-xs uppercase tracking-wide text-emerald-800 flex items-center gap-1">
                  <ArrowDownRight className="h-4 w-4" /> Input VAT (Line 15)
                </div>
                <div className="text-3xl font-bold text-emerald-700 mt-1" data-testid="input-vat-total">
                  {zar(report.summary.input_vat)}
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  VAT paid on GRV/purchase invoices in the period.
                </div>
              </CardContent>
            </Card>
            <Card className={isPayable ? "border-red-200" : "border-green-200"}>
              <CardContent className="pt-6">
                <div className={`text-xs uppercase tracking-wide flex items-center gap-1 ${isPayable ? "text-red-800" : "text-green-800"}`}>
                  <Landmark className="h-4 w-4" /> Net VAT — Line 20
                </div>
                <div className={`text-3xl font-bold mt-1 ${isPayable ? "text-red-700" : "text-green-700"}`} data-testid="net-vat-total">
                  {zar(Math.abs(netPayable))}
                </div>
                <Badge className={`mt-2 ${isPayable ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                  {isPayable ? "PAYABLE TO SARS" : "REFUND FROM SARS"}
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Reconciliation panel */}
          {rec?.attempted && (
            <Card className={recPassed ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {recPassed ? (
                    <><CheckCircle2 className="h-5 w-5 text-green-600" /> Audit Pack reconciliation — {rec.period_label} ✓</>
                  ) : (
                    <><AlertTriangle className="h-5 w-5 text-red-600" /> Audit Pack reconciliation — {rec.period_label} ✗</>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rec.output_vat && rec.input_vat && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="py-2">Metric</th>
                        <th className="py-2 text-right">VAT Report</th>
                        <th className="py-2 text-right">Audit Pack</th>
                        <th className="py-2 text-right">Diff</th>
                        <th className="py-2 text-center w-24">Status</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      <tr className="border-b">
                        <td className="py-2 font-sans">Output VAT</td>
                        <td className="py-2 text-right">{zar(rec.output_vat.vat_report)}</td>
                        <td className="py-2 text-right">{zar(rec.output_vat.audit_pack)}</td>
                        <td className="py-2 text-right">{zar(rec.output_vat.diff)}</td>
                        <td className="py-2 text-center">
                          {rec.output_vat.matches
                            ? <Badge className="bg-green-600 text-white">Match</Badge>
                            : <Badge className="bg-red-600 text-white">Mismatch</Badge>}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 font-sans">Input VAT</td>
                        <td className="py-2 text-right">{zar(rec.input_vat.vat_report)}</td>
                        <td className="py-2 text-right">{zar(rec.input_vat.audit_pack)}</td>
                        <td className="py-2 text-right">{zar(rec.input_vat.diff)}</td>
                        <td className="py-2 text-center">
                          {rec.input_vat.matches
                            ? <Badge className="bg-green-600 text-white">Match</Badge>
                            : <Badge className="bg-red-600 text-white">Mismatch</Badge>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          )}
          {rec && !rec.attempted && (
            <Card className="border-slate-300 bg-slate-50">
              <CardContent className="py-4 text-sm text-slate-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-slate-500" />
                <div>
                  <strong>Reconciliation skipped</strong> — {rec.reason}
                </div>
              </CardContent>
            </Card>
          )}

          {/* VAT201 blocks */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">SARS VAT201 blocks</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 w-16">Line</th>
                    <th className="py-2">Description</th>
                    <th className="py-2 text-right">Amount (R)</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  <tr className="border-b"><td className="py-2">1</td><td className="py-2 font-sans">Standard-rated supplies (INCL VAT)</td><td className="py-2 text-right">{zar(report.vat201.block_1_standard_supplies_incl_vat)}</td></tr>
                  <tr className="border-b"><td className="py-2">2</td><td className="py-2 font-sans">Zero-rated supplies</td><td className="py-2 text-right">{zar(report.vat201.block_2_zero_rated_supplies)}</td></tr>
                  <tr className="border-b"><td className="py-2">3</td><td className="py-2 font-sans">Exempt supplies</td><td className="py-2 text-right">{zar(report.vat201.block_3_exempt_supplies || 0)}</td></tr>
                  <tr className="border-b"><td className="py-2">4</td><td className="py-2 font-sans">Total supplies (INCL VAT)</td><td className="py-2 text-right">{zar(report.vat201.block_4_total_supplies_incl_vat)}</td></tr>
                  <tr className="border-b bg-blue-50"><td className="py-2 font-semibold">4A</td><td className="py-2 font-sans font-semibold">Output VAT</td><td className="py-2 text-right font-bold">{zar(report.vat201.block_4a_output_vat)}</td></tr>
                  <tr className="border-b"><td className="py-2">14A</td><td className="py-2 font-sans">Standard-rated purchases (INCL VAT)</td><td className="py-2 text-right">{zar(report.vat201.block_14a_standard_purchases_incl_vat)}</td></tr>
                  <tr className="border-b bg-emerald-50"><td className="py-2 font-semibold">15</td><td className="py-2 font-sans font-semibold">Input VAT</td><td className="py-2 text-right font-bold">{zar(report.vat201.block_15_input_vat)}</td></tr>
                  <tr className={isPayable ? "bg-red-50" : "bg-green-50"}>
                    <td className="py-2 font-semibold">20</td>
                    <td className="py-2 font-sans font-semibold">Net VAT {isPayable ? "payable to SARS" : "refund from SARS"}</td>
                    <td className="py-2 text-right font-bold">
                      {zar(Math.abs(netPayable))}
                      <Badge className={`ml-2 ${isPayable ? "bg-red-600 text-white" : "bg-green-600 text-white"}`}>
                        {isPayable ? "PAY" : "CLAIM"}
                      </Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Detailed breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Sales breakdown</CardTitle></CardHeader>
              <CardContent>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between border-b py-1"><dt>Gross sales (net of refunds)</dt><dd className="font-mono">{zar(report.sales.gross_incl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Standard-rated (INCL)</dt><dd className="font-mono">{zar(report.sales.standard_rated_incl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Standard-rated (EXCL)</dt><dd className="font-mono">{zar(report.sales.standard_rated_excl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Zero-rated</dt><dd className="font-mono">{zar(report.sales.zero_rated)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Exempt supplies</dt><dd className="font-mono">{zar(report.sales.exempt || 0)}</dd></div>
                  <div className="flex justify-between border-b py-1"><dt>Refunds (INCL)</dt><dd className="font-mono">{zar(report.sales.refunds_incl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1"><dt>Refund VAT credit</dt><dd className="font-mono">{zar(report.sales.refund_vat_credit)}</dd></div>
                  <div className="flex justify-between py-1 font-semibold"><dt>Output VAT</dt><dd className="font-mono">{zar(report.sales.output_vat)}</dd></div>
                  <div className="text-xs text-gray-500 pt-2 flex gap-4">
                    <span>{report.sales.sale_count} sales</span>
                    <span>{report.sales.refund_count} refunds</span>
                  </div>
                </dl>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Purchases (GRV) breakdown</CardTitle></CardHeader>
              <CardContent>
                <dl className="text-sm space-y-1">
                  <div className="flex justify-between border-b py-1"><dt>Total (INCL VAT)</dt><dd className="font-mono">{zar(report.purchases.total_incl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Standard-rated (INCL)</dt><dd className="font-mono">{zar(report.purchases.standard_rated_incl_vat)}</dd></div>
                  <div className="flex justify-between border-b py-1 pl-4 text-gray-600"><dt>Zero-rated</dt><dd className="font-mono">{zar(report.purchases.zero_rated_incl_vat)}</dd></div>
                  <div className="flex justify-between py-1 font-semibold"><dt>Input VAT</dt><dd className="font-mono">{zar(report.purchases.input_vat)}</dd></div>
                  <div className="text-xs text-gray-500 pt-2">{report.purchases.grv_count} GRVs</div>
                </dl>
              </CardContent>
            </Card>
          </div>

          {/* ── Income breakdown by product category × VAT ─────────── */}
          {extras && (
            <Card data-testid="vat-category-breakdown">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tags className="h-4 w-4 text-blue-600" />
                  Income by product category × VAT treatment
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadCategoryXlsx}
                  data-testid="vat-category-xlsx"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Excel
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 mb-3">
                  Category income totals so the accountant can classify each stream on VAT201
                  (Voer/Lusern, Chemikalie, Mieliemeel, etc.). Amounts are shown incl-VAT for
                  standard-rated lines.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2">Category</th>
                        <th className="py-2 text-right">Standard 15% (incl)</th>
                        <th className="py-2 text-right">Zero-rated</th>
                        <th className="py-2 text-right">Exempt / Out of scope</th>
                        <th className="py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {extras.categories.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-4 text-center text-gray-500 italic">
                            No sales in the selected period.
                          </td>
                        </tr>
                      )}
                      {extras.categories.map((r) => (
                        <tr key={r.category} className="border-b">
                          <td className="py-1.5 font-sans">{r.category}</td>
                          <td className="py-1.5 text-right">{zar(r.standard)}</td>
                          <td className="py-1.5 text-right">{zar(r.zero)}</td>
                          <td className="py-1.5 text-right">{zar(r.exempt)}</td>
                          <td className="py-1.5 text-right font-semibold">{zar(r.total)}</td>
                        </tr>
                      ))}
                      {extras.categories.length > 0 && (
                        <tr className="bg-yellow-50 font-semibold">
                          <td className="py-2 font-sans">Total</td>
                          <td className="py-2 text-right">{zar(extras.totals.standard)}</td>
                          <td className="py-2 text-right">{zar(extras.totals.zero)}</td>
                          <td className="py-2 text-right">{zar(extras.totals.exempt)}</td>
                          <td className="py-2 text-right">{zar(extras.totals.total)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ── Zero-rated compliance audit ─────────────────────────── */}
          {extras && (
            <Card
              className={
                extras.non_compliant_zero_rated.count > 0
                  ? "border-red-300 bg-red-50"
                  : "border-green-300 bg-green-50"
              }
              data-testid="vat-zero-rated-audit"
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {extras.non_compliant_zero_rated.count > 0 ? (
                    <ShieldAlert className="h-5 w-5 text-red-600" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  Zero-rated compliance — Farmer VAT certificate audit
                </CardTitle>
              </CardHeader>
              <CardContent>
                {extras.non_compliant_zero_rated.count === 0 ? (
                  <p className="text-sm text-green-800">
                    All zero-rated sales in this period are backed by a valid Farmer VAT
                    certificate on the customer record. ✓
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-red-800 mb-3">
                      <strong>{extras.non_compliant_zero_rated.count}</strong> zero-rated line(s)
                      totalling <strong>{zar(extras.non_compliant_zero_rated.total_amount)}</strong>{" "}
                      were sold WITHOUT a valid Farmer VAT certificate. SARS will disallow the
                      zero-rating on verification and re-rate these sales at 15%. Add each
                      customer's certificate on their profile before submitting VAT201.
                    </p>
                    <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded bg-white">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-100">
                          <tr className="text-left">
                            <th className="py-2 px-2">Date</th>
                            <th className="py-2 px-2">Invoice</th>
                            <th className="py-2 px-2">Customer</th>
                            <th className="py-2 px-2">Product</th>
                            <th className="py-2 px-2">Category</th>
                            <th className="py-2 px-2 text-right">Amount</th>
                            <th className="py-2 px-2">Cert?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {extras.non_compliant_zero_rated.lines.map((l, i) => (
                            <tr key={i} className="border-b">
                              <td className="py-1.5 px-2">{l.sale_date}</td>
                              <td className="py-1.5 px-2 font-mono">{l.invoice_number}</td>
                              <td className="py-1.5 px-2">{l.customer_name}</td>
                              <td className="py-1.5 px-2">{l.product_name}</td>
                              <td className="py-1.5 px-2">{l.category}</td>
                              <td className="py-1.5 px-2 text-right font-mono">{zar(l.amount)}</td>
                              <td className="py-1.5 px-2">
                                <Badge
                                  className={
                                    l.cert_on_file
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-red-100 text-red-800"
                                  }
                                >
                                  {l.cert_on_file ? "Expired" : "Missing"}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Exports */}
          <div className="flex gap-3 justify-end flex-wrap">
            <Button variant="outline" onClick={downloadCsv} data-testid="vat-export-csv">
              <Download className="h-4 w-4 mr-1" />
              Export CSV
            </Button>
            <Button className="bg-slate-700 hover:bg-slate-800" onClick={downloadPdf} data-testid="vat-export-pdf">
              <FileText className="h-4 w-4 mr-1" />
              Print / Save as PDF
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2">
            Report for <strong>{fmtDate(report.period.start)} → {fmtDate(report.period.end)}</strong> — please verify against your accounting records before filing VAT201 with SARS.
          </p>
        </>
      )}
    </div>
  );
}
